/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { EditorRefPlugin } from "@lexical/react/LexicalEditorRefPlugin";
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbLinkNode } from "abreact/input/markdown/nodes/AbLinkNode";
import { AbstractFieldNode } from "abreact/input/markdown/nodes/AbstractFieldNode";
import { PersonNode } from "abreact/input/markdown/nodes/PersonNode";
import AbstractFieldPlugin from "abreact/input/markdown/plugins/AbstractFieldPlugin";
import ExampleTheme from 'abreact/input/markdown/ui/ExampleTheme';
import { convertFromMarkdownString } from "abreact/input/markdown/utils/MarkdownImport";
import { AB_MARKDOWN_TRANSFORMERS } from "abreact/input/markdown/utils/MarkdownTransformers";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
import { $getRoot, COMMAND_PRIORITY_LOW, createCommand, LineBreakNode } from "lexical";
import * as React from "react";
import { ImageNode } from "./nodes/ImageNode";
// DEBUG ONLY: import TreeViewPlugin from "abreact/input/markdown/plugins/TreeViewPlugin";

const editorConfig = {
  namespace: 'AbMarkdownLexicalContent',
  nodes: [HeadingNode, ListNode, ListItemNode, QuoteNode, CodeNode,
  // CodeHighlightNode,
  TableNode, TableCellNode, TableRowNode, AutoLinkNode, LinkNode, ImageNode, HorizontalRuleNode, AbLinkNode, PersonNode, AbstractFieldNode, LineBreakNode],
  // Handling of errors during update
  onError(error) {
    console.error(error);
  },
  // The editor theme
  theme: ExampleTheme
};
export const TEST_UPDATE_COMMAND = createCommand();
export const TEST_DATA_LOADED = createCommand();
export function AbMarkdownLexicalContent(props) {
  const tempRef = React.useRef(null);
  const editorRef = props.editorRef ?? tempRef;

  // value of markdown
  let [markdown, setMarkdown] = React.useState(props.value);
  const {
    disabled,
    ...inputProps
  } = ReactUtils.inputProps("ab-markdown-editor", props, [props.resizable && "ab-resizable"]);

  // initial editor converts markdown from the props
  let config = {
    ...editorConfig,
    editable: false,
    editorState: () => convertFromMarkdownString(markdown || "", AB_MARKDOWN_TRANSFORMERS)
  };
  React.useEffect(() => {
    if (props.value === "") {
      editorRef.current?.update(() => {
        $getRoot().clear();
      });
    } else if (props.value !== markdown) {
      editorRef.current?.update(() => {
        convertFromMarkdownString(props.value, AB_MARKDOWN_TRANSFORMERS);
      });
      setMarkdown(props.value);
    }
  }, [editorRef, markdown, props.value]);
  React.useEffect(() => {
    editorRef.current?.registerCommand(TEST_DATA_LOADED, () => {
      editorRef.current?.update(() => {
        convertFromMarkdownString(markdown, AB_MARKDOWN_TRANSFORMERS);
      });
      return false;
    }, COMMAND_PRIORITY_LOW);
  }, [editorRef, markdown]);
  return /*#__PURE__*/React.createElement(AbWithTooltip, {
    tooltip: props.tooltip
  }, /*#__PURE__*/React.createElement("div", inputProps, /*#__PURE__*/React.createElement(LexicalComposer, {
    initialConfig: config
  }, /*#__PURE__*/React.createElement("div", {
    className: "editor-inner"
  }, /*#__PURE__*/React.createElement(RichTextPlugin, {
    contentEditable: /*#__PURE__*/React.createElement("div", {
      className: "content-editor"
    }, /*#__PURE__*/React.createElement(ContentEditable, null)),
    placeholder: props.placeholder ? /*#__PURE__*/React.createElement("div", {
      className: "placeholder"
    }, props.placeholder) : null,
    ErrorBoundary: LexicalErrorBoundary
  }), /*#__PURE__*/React.createElement(EditorRefPlugin, {
    editorRef: x => {
      editorRef.current = x;
    }
  }), /*#__PURE__*/React.createElement(AbstractFieldPlugin, {
    viewMode: "test"
  })))));
}
//# sourceMappingURL=AbMarkdownLexicalContent.js.map
